import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, FileText, AlertCircle, RefreshCw, Eye, Search, Filter,
  MoreVertical, ChevronDown
} from 'lucide-react';
import { apiFetch } from '../services/api';
import './Cobrancas.css';

export default function CobrancasAvulsas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Verificação do Atendente baseada nas permissões
  const userPermissions = user?.permissions || [];
  const isAtendente = userPermissions.length > 0 && 
                      !userPermissions.includes('administracao') && 
                      !userPermissions.includes('relatorios') &&
                      !userPermissions.includes('gerar-cobranca');
  
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  // Carrega pagamentos automaticamente APENAS se NÃO for Atendente
  useEffect(() => {
    if (isAtendente) {
      setLoading(false);
      setPagamentos([]);
      return;
    }

    const carregarPagamentos = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/asaas/local-payments?limit=500');
        const dados = response.data || [];
        // Filtra apenas cobranças avulsas (SINGLE)
        const avulsas = dados.filter(p => 
          p.billingType === 'SINGLE' || 
          (p.subscription_type && p.subscription_type === 'SINGLE')
        );
        setPagamentos(avulsas);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar as cobranças avulsas.');
      } finally {
        setLoading(false);
      }
    };
    carregarPagamentos();
  }, [isAtendente]);

  // Função de busca via API (para Atendente)
  const realizarBusca = async () => {
    const term = searchTerm.trim();
    
    if (!term) {
      setPagamentos([]);
      setBuscaRealizada(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/asaas/local-payments?limit=500&search=${encodeURIComponent(term)}`);
      let dados = response.data || [];
      
      // Filtro manual para garantir resultados relevantes
      const termLower = term.toLowerCase();
      dados = dados.filter(p => {
        const name = (p.customerName || '').toLowerCase();
        const email = (p.customerEmail || '').toLowerCase();
        const cpf = (p.customerCpf || p.cpf || '').replace(/\D/g, '');
        const description = (p.description || '').toLowerCase();
        const termDigits = term.replace(/\D/g, '');
        
        const match = name.includes(termLower) || 
                      email.includes(termLower) || 
                      description.includes(termLower) ||
                      (termDigits && cpf.includes(termDigits));
        
        return match;
      });
      
      // Filtra apenas cobranças avulsas (SINGLE)
      const avulsas = dados.filter(p => 
        p.billingType === 'SINGLE' || 
        (p.subscription_type && p.subscription_type === 'SINGLE')
      );
      
      setPagamentos(avulsas);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      setError('Não foi possível realizar a busca.');
      setPagamentos([]);
      setBuscaRealizada(true);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const getStatusBadge = (status) => {
    const classes = {
      'RECEIVED': 'cob-status-received',
      'CONFIRMED': 'cob-status-confirmed',
      'PENDING': 'cob-status-pending',
      'OVERDUE': 'cob-status-overdue'
    };
    const labels = {
      'RECEIVED': 'Recebido',
      'CONFIRMED': 'Confirmado',
      'PENDING': 'Pendente',
      'OVERDUE': 'Vencido'
    };
    return <span className={`cob-status-badge ${classes[status] || ''}`}>{labels[status] || status}</span>;
  };

  return (
    <div className="cobrancas-page-container">
      {/* Header */}
      <div className="cobrancas-page-header">
        <h1 className="cobrancas-page-title">Cobranças Avulsas</h1>
      </div>

      {/* Card Principal */}
      <div className="cobrancas-card">
        {/* Toolbar */}
        <div className="cobrancas-toolbar">
          <div className="cobrancas-toolbar-left">
            <div className="cobrancas-search-box">
              <input
                type="text"
                placeholder={isAtendente ? "Digite o nome ou email do cliente para buscar" : "Procurar por nome ou email do cliente"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    realizarBusca();
                  }
                }}
                className="cobrancas-search-input"
              />
              <button 
                className="cobrancas-search-btn" 
                title="Buscar"
                onClick={realizarBusca}
              >
                <Search size={16} />
              </button>
            </div>
            <button className="cobrancas-btn-filter">
              <Filter size={16} />
              Filtros
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="cobrancas-toolbar-right">
            <button className="cobrancas-btn-batch">
              <MoreVertical size={16} />
              Ações em lote
              <ChevronDown size={16} />
            </button>
            <button className="cobrancas-btn-add" onClick={() => navigate('/gerar-cobranca?tipo=avulsa')}>
              <span style={{ fontSize: 18, marginRight: 4 }}>+</span>
              Adicionar cobrança
            </button>
          </div>
        </div>

        {loading && (
          <div className="cobrancas-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Carregando cobranças avulsas...</p>
          </div>
        )}

        {error && (
          <div className="cobrancas-error">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        )}

        {/* Mensagem inicial para Atendente */}
        {isAtendente && !buscaRealizada && !loading && !error && (
          <div className="cobrancas-empty">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '12px',
              padding: '40px 20px',
              color: '#64748b'
            }}>
              <Search size={48} style={{ color: '#94a3b8' }} />
              <h3 style={{ margin: 0, color: '#334155', fontSize: '18px' }}>
                Realize uma busca para visualizar as cobranças avulsas
              </h3>
              <p style={{ margin: 0, textAlign: 'center', maxWidth: '400px', fontSize: '14px' }}>
                Digite o nome ou email do cliente no campo de busca acima e pressione Enter ou clique no ícone de lupa.
              </p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && pagamentos.length === 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-empty">
            <FileText size={48} />
            <p>Nenhuma cobrança avulsa encontrada.</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && pagamentos.length > 0 && (buscaRealizada || !isAtendente) && (
          <div className="cobrancas-table-wrapper">
            <table className="cobrancas-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Descrição</th>
                  <th>Data Pagamento</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.map(p => (
                  <tr key={p.id}>
                    <td>{p.customerName || '—'}</td>
                    <td className="valor">{formatarMoeda(p.value)}</td>
                    <td>{formatarData(p.dueDate)}</td>
                    <td>{getStatusBadge(p.status)}</td>
                    <td>{p.description || '—'}</td>
                    <td>{formatarData(p.receivedDate || p.paymentDate)}</td>
                    <td>
                      <button 
                        className="cob-btn-view" 
                        title="Ver detalhes"
                        onClick={() => window.open(p.invoiceUrl || '#', '_blank')}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}